/******************************************
 * GROUP SEARCH AND FILTERING WITH TAGS
 * + SELECT ALL THAT CAN BE MANUALLY EDITED
 *****************************************/

import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { withTranslation } from "react-i18next"
import { getGroups } from "../../helpers/backend_helper"
import { Select, ConfigProvider } from "antd"
import DropdownWithSelectAll from "./DropdownWithSelectAll"

function renderPopupRender(menu, props, onSelectAll) {
  return (
    <DropdownWithSelectAll
      menu={menu}
      showSelectAll={props.multiple}
      onSelectAll={onSelectAll}
      label={props.t("Select all")}
    />
  )
}

const SelectGroupFilters = props => {
  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState([])
  const [origOptions, setOrigOptions] = useState([])
  const [selectedTags, setSelectedTags] = useState([])
  const [temp, setTemp] = useState([])
  const [selectedValues, setSelectedValues] = useState([])

  const abortRef = useRef(null)

  useEffect(() => {
    abortRef.current = new AbortController()
    getData()
    return () => abortRef.current.abort()
  }, [])

  useEffect(() => {
    if (props.updateCounter > 0) {
      getData()
    }
  }, [props.updateCounter])

  useEffect(() => {
    if (props.selectedTags !== undefined) {
      setSelectedTags(props.selectedTags)
    }
  }, [props.selectedTags])

  useEffect(() => {
    //console.log("(SelectGroup) Selected tags: ", selectedTags)
    if (props.selection) {
      setSelectedValues(props.selection)
    }
  }, [props.selection])

  useEffect(() => {
    if (selectedTags?.length > 0) {
      setTemp([])
      for (const o of origOptions) {
        for (const tag of selectedTags) {
          if (o.tag.includes(tag.toString())) {
            setTemp(filtered => [...filtered, o])
          }
        }
      }
    }
    if (selectedTags?.length === 0) {
      setOptions(origOptions)
    }
  }, [selectedTags])

  useEffect(() => {
    let setNew = Array.from(new Set(temp))
    setOptions(prev => {
      const selectAll = prev.find(o => o.value === "__ALL__")
      return selectAll ? [selectAll, ...setNew] : setNew
    })
  }, [temp])

  // Get groups
  const getData = () => {
    setLoading(true)
    getGroups(abortRef.current, props.strictMode ?? true)
      .then(result => {
        if (result.entries !== null) {
          let opts = []
          let counter = 1
          for (const group of result.entries) {
            let groupObject = {
              id: counter++,
              label: group.name,
              value: group.name,
              tag: Settingtostring(group.tags),
            }
            opts.push(groupObject)
          }

          // Add "Select All" as a visual option if desired
          opts.unshift({
            id: 0,
            label: props.t("Select all"),
            value: "__ALL__",
            tag: "",
          })

          setOptions(opts)
          setOrigOptions(opts)
        } else {
          setOptions([])
          handleChange(null)
        }
      })
      .catch(console.log)
      .finally(() => {
        setLoading(false)
      })
  }

  // Setting tags to string
  const Settingtostring = arr => {
    let string = []
    if (arr.length > 0) {
      for (const g of arr) {
        let tag = g.key
        string.push(tag)
      }
    }
    return string.toString()
  }

  // Group selection changed
  const handleChange = values => {
    setSelectedValues(values)
    if (props.onChange) {
      props.onChange(values)
    }
  }

  const handleVisibilityChange = state => {
    if (props.onVisibilityChange != null) {
      props.onVisibilityChange(state)
    }
  }

  // Custom empty/Data not found dropdown
  const customizeRenderEmpty = () => (
    <div className="mt-3 text-center">
      {loading ? (
        <p>{props.t("Loading")}</p>
      ) : (
        <p>{props.t("No groups found")}</p>
      )}
    </div>
  )

  const handleSelectAll = () => {
    const allValues = options
      .filter(o => o.value !== "__ALL__")
      .map(o => o.value)

    const isAllSelected =
      allValues.length === selectedValues.length &&
      allValues.every(v => selectedValues.includes(v))

    const newValues = isAllSelected ? [] : allValues

    setSelectedValues(newValues)
    if (props.onChange) {
      props.onChange(newValues)
    }
  }

  return (
    <div>
      <ConfigProvider renderEmpty={customizeRenderEmpty}>
        <div className="config-provider">
          <Select
            showSearch
            disabled={props.disabled}
            loading={loading}
            value={selectedValues}
            mode={props.multiple ? "multiple" : null}
            allowClear={props.clearable != null ? props.clearable : true}
            maxTagCount={10}
            placeholder={
              props.multiple
                ? props.t("Select groups")
                : props.t("Select group")
            }
            style={{ width: "100%" }}
            onChange={handleChange}
            options={options.filter(o => o.value !== "__ALL__")}
            onOpenChange={handleVisibilityChange}
            popupRender={menu =>
              renderPopupRender(menu, props, handleSelectAll)
            }
          />
        </div>
      </ConfigProvider>
    </div>
  )
}

SelectGroupFilters.propTypes = {
  t: PropTypes.any,
  selection: PropTypes.any,
  onChange: PropTypes.func,
  onVisibilityChange: PropTypes.func,
  multiple: PropTypes.bool,
  clearable: PropTypes.bool,
  disabled: PropTypes.bool,
  updateCounter: PropTypes.any,
  selectedTags: PropTypes.any,
  strictMode: PropTypes.bool,
}

export default withTranslation()(SelectGroupFilters)
